/**
 * FilterValueLabel — the label the filter chip shows for a condition's value,
 * rendered after a condition shown as text (the Visual Query Editor's WHERE /
 * HAVING pills): ` — Acme Corp` for a lookup key, ` — Active` for a
 * lookup-enum key. Renders nothing while the label loads or when none
 * resolves, so the value reads as written.
 *
 * Same sources as the chip, so no extra query per render:
 *  - lookup (top-level `lookup`, or a JSON `property_lookup` pick — the JSON
 *    FK-picker's scalar shape) → `useFkLookupLabel` (the chip's lookup
 *    button: one cached row read per target + key), keyed by the condition's
 *    `sourcePath` (top-level) or `path` (JSON pick) — see
 *    `lookupConditionSourcePath`;
 *  - lookup enum → the cached key / label pairs in MetaInfo
 *    (`useLookupLabelMaps`, the results grids' label source) — never a fetch.
 *
 * The JSON `property_enum` sub-operator needs no separate label: its declared
 * values are always sampled literals (`EnumColumnDeclaration.path` implies
 * `source: 'sampled'`), so the value shown IS its own label already.
 */

import { useMemo } from 'react';

import type { DatabaseMetaInfo } from '../../../../../types/electron';
import type { FilterCondition, TableExplorerColumn } from '../../../core/types/workspaceTypes';
import { useFkLookupLabel } from '../../hooks/useFkLookupLabel';
import { useLookupLabelMaps } from '../../hooks/useLookupLabelMaps';
import { enumConditionLabelText, lookupConditionKey, lookupConditionSourcePath, lookupEnumTargetFor } from '../../utils/filterValueLabels';
import { hasRealForeignKeyTarget, looseTargetFilterForColumn } from '../../utils/looseFkResolver';

export type FilterValueLabelProps = {
  readonly condition: FilterCondition;
  readonly column: TableExplorerColumn;
  /** The condition's table — its MetaInfo identity. */
  readonly schema: string;
  readonly table: string;
  readonly connectionId: string;
  readonly database: string;
  readonly metaInfo: DatabaseMetaInfo | null | undefined;
  readonly testId?: string;
};

const LABEL_CLASS = 'font-sans text-muted-foreground';

export function FilterValueLabel(props: FilterValueLabelProps) {
  if (lookupConditionKey(props.condition) !== null) return <LookupValueLabel {...props} />;
  if (props.condition.operator === 'enum') return <EnumValueLabel {...props} />;
  return null;
}

function LookupValueLabel({ condition, column, schema, table, connectionId, database, metaInfo, testId }: FilterValueLabelProps) {
  const label = useFkLookupLabel({
    connectionId,
    database,
    sourceColumn: column.name,
    sourceSchema: schema,
    sourceTable: table,
    referencedSchema: column.referencedSchema ?? '',
    referencedTable: column.referencedTable ?? '',
    referencedColumn: column.referencedColumn ?? '',
    referencesRealForeignKey: hasRealForeignKeyTarget(column),
    targetFilter: looseTargetFilterForColumn(metaInfo?.looseRelationships, schema, table, column.name, column),
    sourcePath: lookupConditionSourcePath(condition),
    value: lookupConditionKey(condition),
  });
  return label === null ? null : (
    <span className={LABEL_CLASS} data-testid={testId}>{` — ${label}`}</span>
  );
}

function EnumValueLabel({ condition, column, schema, table, metaInfo, testId }: FilterValueLabelProps) {
  const columns = useMemo(
    () => [{ id: 'value', sourceSchema: schema, sourceTable: table, sourceColumn: column.name }],
    [schema, table, column.name],
  );
  const target = useMemo(() => lookupEnumTargetFor(column, schema, table, metaInfo), [column, schema, table, metaInfo]);
  const fkLookupTargets = useMemo(() => (target ? new Map([['value', target]]) : undefined), [target]);
  const labels = useLookupLabelMaps({ metaInfo, columns, fkLookupTargets }).get('value')?.labels;
  const text = enumConditionLabelText(condition.value, labels);
  return text === null ? null : (
    <span className={LABEL_CLASS} data-testid={testId}>{` — ${text}`}</span>
  );
}
